import DeviceConfig from '@devices/config';
import Direction from '../../../../shared/types/enums/direction';
import Focus from '../../../../shared/types/enums/focus';
import Item from './menuItem';
import IMenuItem from '../../../../shared/types/interfaces/menuItem';
import List from '../../sections/list/list';
import Modals from '../../../../shared/types/enums/modals';
import Pages from '../../../../shared/types/enums/pages';
import Selector from './menuSelector';
import TranslationManager from '../../../../shared/assets/locale/translationsManager';
import useKeyboard from '../../../../shared/components/utils/hooks/useKeyboard';
import { magicRemoteSelector } from '../../../../shared/store/slices/magicRemote.slice';
import { setAds } from '../../../../shared/store/slices/ads.slice';
import { setModal } from '../../../../shared/store/slices/modal.slice';
import { store } from '../../../../shared/store/store';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { userSelector } from '../../../../shared/store/slices/user.slice';
import {
  clearStack,
  emptyRoute,
  routerSelector,
} from '../../../../shared/store/slices/router.slice';
import {
  focusSelector,
  popFocus,
  pushFocus,
  setFocus,
} from '../../../../shared/store/slices/focus.slice';

const WAITING_TIME = 450;

function MenuContent({
  Data,
  display,
  index,
}: {
  Data: IMenuItem[];
  display: boolean;
  index: number;
}) {
  const dispatch = useAppDispatch();
  const { focus } = useAppSelector(focusSelector);
  const { isAnonymous } = useAppSelector(userSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);
  const {
    route,
    params: { current: currentParams },
  } = useAppSelector(routerSelector);

  const [, setIndex] = useState(index);
  const [arrowIndex, setArrowIndex] = useState(index);

  const arrowIndexRef = useRef<number>(index);
  const currentIndexRef = useRef<number>(index);
  const dataRef = useRef<IMenuItem[]>([]);
  const focusRef = useRef(focus);
  const focusedRef = useRef<boolean>(false);
  const isAnonymousRef = useRef(isAnonymous);
  const lastIndexRef = useRef<number>(index);
  const paramsRef = useRef(currentParams);
  const pageRef = useRef(route.Page);
  arrowIndexRef.current = arrowIndex;
  dataRef.current = Data;
  focusRef.current = focus;
  focusedRef.current = focus === Focus.MENU;
  isAnonymousRef.current = isAnonymous;
  pageRef.current = route.Page;
  paramsRef.current = currentParams;

  const setListIndex = useRef<(newIndex: number, increment?: number) => void>(
    () => null
  );
  const selectorTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const selectorFocused = focus === Focus.MENU && !enabled;

  // Event handlers

  const onChange = useCallback((index: number) => {
    if (index < 0 || index === arrowIndexRef.current) return;
    if (!focusedRef.current) return;
    arrowIndexRef.current = index;
    setArrowIndex(index);
    clearTimeout(selectorTimeout.current);
    dispatch(emptyRoute());
    selectorTimeout.current = setTimeout(() => {
      setIndex((last) => {
        dispatch(clearStack());
        setTimeout(() => {
          dataRef.current[index].action();
        });
        currentIndexRef.current = index;
        lastIndexRef.current = last;
        return index;
      });
    }, WAITING_TIME);
  }, []);

  const onEnter = useCallback((index: number, key?: string) => {
    if (!focusedRef.current) return;
    // Enter page if on Detail or Search and key is not 'Right'
    if (
      [Pages.CHANNEL_LIST, Pages.DETAIL, Pages.SEARCH].includes(
        pageRef.current
      ) &&
      key !== 'RIGHT'
    ) {
      dataRef.current[index].action();
    }
    // Trigger ad on enter
    else {
      const adSource = dataRef.current[index].ad();
      dispatch(setAds(adSource));
      dispatch(popFocus());
    }
  }, []);

  const onFocus = useCallback((index: number) => {
    if (focusRef.current !== Focus.MENU) {
      dispatch(setFocus(Focus.MENU));
    }
    dataRef.current[index].action();
  }, []);

  // Context actions

  const outOfBoundsAction = useCallback(
    (listFocus: Focus | number | string, key?: string) => {
      if (key === 'UP') {
        if (isAnonymousRef.current && listFocus === Focus.MENU) {
          dispatch(pushFocus(Focus.LOGIN));
        }
      }
    },
    []
  );

  // Effect hooks

  useEffect(() => {
    if (focusRef.current === -1 && !paramsRef.current?.previousFocus) {
      dispatch(pushFocus(Focus.MENU));
    }
    return () => {
      clearTimeout(selectorTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (dataRef.current && index < dataRef.current.length) {
      onChange(index);
      setListIndex.current(index);
    }
  }, [index]);

  useEffect(() => {
    if (
      Data.length &&
      !enabled &&
      focus === Focus.MENU &&
      lastIndexRef.current === -1
    ) {
      setListIndex.current(0);
      Data[0].action();
    }
  }, [Data, enabled, focus]);

  useEffect(() => {
    if (Data.length && display && enabled && currentIndexRef.current >= 0) {
      const adSource = Data[currentIndexRef.current].ad();
      dispatch(setAds(adSource));
    }
  }, [Data, display, enabled]);

  // Keyboard hook

  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key, pressed) => {
      if (
        key === 'BACK' &&
        (pressed || DeviceConfig.capabilities.exitFromMenu)
      ) {
        dispatch(setModal(Modals.EXIT_APP));
      }
    },
    keyUpHandler: (key) => {
      switch (key) {
        case 'RIGHT':
        case 'SELECT':
          const { router } = store.getState();
          if (router.route.Page !== Pages.EMPTY) {
            onEnter(currentIndexRef.current, key);
          }
          break;
      }
    },
  });

  return (
    <>
      <List
        direction={Direction.VERTICAL}
        focus={Focus.MENU}
        focused={focusedRef.current}
        initialIndex={index}
        onChange={onChange}
        outOfBounds={outOfBoundsAction}
        setListIndex={setListIndex}
      >
        {Data.map((item, index) => (
          <Item
            index={index}
            key={index}
            enterAction={onEnter}
            focusAction={onFocus}
          >
            {TranslationManager.get(item?.text)}
          </Item>
        ))}
      </List>
      <Selector index={arrowIndex} focused={selectorFocused} />
    </>
  );
}

export default MenuContent;
